/**
 * Admin-only: the stream connectivity self-test, and the persisted log of
 * whatever the stream Worker and the player have reported going wrong.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ErrorLogEntry } from '@sflx/shared';
import { diagnostics, errorLog, type StreamDiagnosticsResult } from '../lib/api';
import { ErrorNotice, Loading, Spinner } from '../components/Feedback';
import { Icon } from '../components/Icon';
import { formatDateTime, formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import { navigate } from '../router/router';

const SOURCE_LABEL: Record<ErrorLogEntry['source'], string> = { stream: 'Stream', player: 'Player' };

export function Settings() {
  const { user } = useSession();
  const [rows, setRows] = useState<ErrorLogEntry[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<StreamDiagnosticsResult | null>(null);

  const reload = useCallback(async () => {
    const result = await errorLog.list(100);
    setRows(result.items);
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/accounts');
      return;
    }
    void reload().catch((caught: unknown) => setError(caught));
  }, [reload, user?.role]);

  const runCheck = async () => {
    setChecking(true);
    setCheck(null);
    try {
      setCheck(await diagnostics.stream());
    } catch (caught) {
      setError(caught);
    } finally {
      setChecking(false);
    }
  };

  if (rows === null && !error) {
    return (
      <div className="page">
        <Loading label="Loading settings" />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="wrap stack">
        <div className="page__head">
          <div className="page__title">
            <h1>Settings</h1>
            <span className="muted">Connectivity checks and the error log for the streaming pipeline.</span>
          </div>
        </div>

        <section className="card stack">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>Stream connectivity</strong>
            <button type="button" className="btn btn--sm" onClick={() => void runCheck()} disabled={checking}>
              {checking ? <Spinner /> : <Icon name="refresh" size={14} />}
              Check
            </button>
          </div>
          {check ? (
            <div className={`notice ${check.ok ? '' : 'notice--error'}`}>
              <div className="notice__title">{check.ok ? 'The stream Worker is reachable.' : 'Something is wrong.'}</div>
              {check.hint ? <div className="notice__detail">{check.hint}</div> : null}
            </div>
          ) : (
            <span className="muted">Verifies the API and stream Workers agree on the same key and release.</span>
          )}
        </section>

        <section className="stack">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>Error log</strong>
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => void reload()}>
              <Icon name="refresh" size={14} />
              Refresh
            </button>
          </div>

          <ErrorNotice error={error} />

          {rows?.length ? (
            <ul className="stack stack--tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {rows.map((row) => (
                <li key={row.id} className="card stack stack--tight">
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="row" style={{ gap: 'var(--s-2)' }}>
                      <Icon name="alertTriangle" size={14} />
                      <span className="badge">{SOURCE_LABEL[row.source]}</span>
                      <span className="mono muted">{row.code}</span>
                    </span>
                    <span className="muted" title={formatDateTime(row.createdAt)}>
                      {formatRelative(row.createdAt)}
                    </span>
                  </div>
                  <span>{row.message}</span>
                  {row.detail ? <span className="muted">{row.detail}</span> : null}
                </li>
              ))}
            </ul>
          ) : error ? null : (
            <p className="muted">Nothing has gone wrong yet.</p>
          )}
        </section>
      </div>
    </div>
  );
}
